import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    BookOpenText, UploadSimple, FileText, ArrowsClockwise, Trash, ShieldCheck, ShieldWarning,
    FileArrowUp, Eye,
} from "@phosphor-icons/react";

/**
 * KnowledgeBaseCard — Settings panel that lets an admin manage the
 * single authoritative HAEVN Knowledge Base document.
 *
 * Behaviour:
 * - Shows the active doc summary (title, type, size, word count, version,
 *   uploaded_by). If none, prompts the admin to upload one.
 * - Upload accepts .pdf, .md, .markdown, .txt via multipart, OR a paste-
 *   text dialog (text/markdown).
 * - "Re-validate all" runs the validator across every non-Published,
 *   non-Archived asset and surfaces a toast summary.
 * - "Preview" opens a modal with the parsed full text + section count
 *   so the admin can confirm the parse looked right.
 */
export default function KnowledgeBaseCard() {
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [revalidating, setRevalidating] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDoc, setPreviewDoc] = useState(null);
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

    const reload = async () => {
        try {
            const r = await api.get("/content-studio/knowledge-base");
            setActive(r.data.active);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load Knowledge Base");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/content-studio/knowledge-base");
                if (!cancelled) setActive(r.data.active);
            } catch (e) {
                if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load Knowledge Base");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File too large (max 10MB)");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const token = localStorage.getItem("haevn:auth_token");
            const resp = await fetch(`${apiBase}/content-studio/knowledge-base/upload`, {
                method: "POST",
                body: form,
                headers: { "Authorization": `Bearer ${token}` },
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `Upload failed (${resp.status})`);
            }
            const data = await resp.json();
            setActive(data.active);
            toast.success("Knowledge Base updated", {
                description: `${data.active.word_count} words · v${data.active.version}`,
            });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const wipe = async () => {
        if (!window.confirm("Delete the active Knowledge Base? All future content will generate WITHOUT fact grounding until a new doc is uploaded.")) return;
        try {
            await api.delete("/content-studio/knowledge-base");
            setActive(null);
            toast.success("Knowledge Base cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not clear KB");
        }
    };

    const revalidateAll = async () => {
        if (!window.confirm("Re-validate every non-Published asset against the current Knowledge Base? This can take a minute for large libraries.")) return;
        setRevalidating(true);
        try {
            const r = await api.post("/content-studio/knowledge-base/revalidate");
            toast.success("Re-validation complete", {
                description: `${r.data.validated} validated · ${r.data.needs_review} flagged · ${r.data.pass} pass · ${r.data.skipped} skipped`,
                duration: 8000,
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-validation failed");
        } finally {
            setRevalidating(false);
        }
    };

    const openPreview = async () => {
        try {
            const r = await api.get("/content-studio/knowledge-base/full");
            setPreviewDoc(r.data.active);
            setPreviewOpen(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load preview");
        }
    };

    if (loading) return <p className="text-xs text-muted-foreground">Loading Knowledge Base…</p>;

    return (
        <section className="border border-border bg-card rounded-sm p-6 mb-6" data-testid="knowledge-base-card">
            <header className="mb-4 pb-3 border-b border-border flex items-center gap-2">
                <BookOpenText size={18} weight="duotone" className="text-primary" />
                <div>
                    <h3 className="font-display text-lg font-bold leading-tight">Knowledge Base</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        The single source of truth for HAEVN product facts. Injected into every content generation and used by the fact-check layer to block unsupported claims (demographics, cadence, pricing, markets, features).
                    </p>
                </div>
            </header>

            {active ? (
                <ActiveDocState
                    doc={active}
                    onFile={onFile}
                    uploading={uploading}
                    onWipe={wipe}
                    onRevalidate={revalidateAll}
                    revalidating={revalidating}
                    onPaste={() => setPasteOpen(true)}
                    onPreview={openPreview}
                />
            ) : (
                <EmptyState
                    onFile={onFile}
                    uploading={uploading}
                    onPaste={() => setPasteOpen(true)}
                />
            )}

            <PasteDialog
                open={pasteOpen}
                onOpenChange={setPasteOpen}
                onSaved={(doc) => { setActive(doc); setPasteOpen(false); }}
            />

            <PreviewDialog
                open={previewOpen}
                onOpenChange={setPreviewOpen}
                doc={previewDoc}
            />
        </section>
    );
}

function ActiveDocState({ doc, onFile, uploading, onWipe, onRevalidate, revalidating, onPaste, onPreview }) {
    const sizeKb = Math.max(1, Math.round((doc.size_bytes || 0) / 1024));
    return (
        <div>
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3 flex items-start gap-3" data-testid="kb-active">
                <ShieldCheck size={20} weight="duotone" className="text-emerald-600 dark:text-emerald-300 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-display text-sm font-semibold truncate">{doc.title || "HAEVN Knowledge Base"}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                        v{doc.version} · {doc.source_type} · {doc.word_count} words · {sizeKb} KB · uploaded by {doc.uploaded_by || "—"}
                    </p>
                    {doc.original_filename && (
                        <p className="text-[11px] text-muted-foreground font-mono truncate">↳ {doc.original_filename}</p>
                    )}
                </div>
                <Button
                    variant="ghost" size="sm" className="rounded-sm h-7 px-2 text-[11px]"
                    onClick={onPreview} data-testid="kb-preview-btn"
                >
                    <Eye size={11} className="mr-1" /> Preview
                </Button>
            </div>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept=".pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain"
                        onChange={onFile}
                        className="hidden"
                        data-testid="kb-replace-file"
                    />
                    <span className={`inline-flex items-center gap-1 rounded-sm border border-primary/40 text-primary px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider hover:bg-primary/5 ${uploading ? "opacity-50" : ""}`}>
                        <FileArrowUp size={12} /> {uploading ? "Uploading…" : "Replace with file"}
                    </span>
                </label>
                <Button
                    variant="outline" size="sm" className="rounded-sm h-8 text-[11px]"
                    onClick={onPaste} data-testid="kb-replace-paste"
                >
                    <FileText size={12} className="mr-1" /> Replace with pasted text
                </Button>
                <Button
                    variant="outline" size="sm" className="rounded-sm h-8 text-[11px]"
                    onClick={onRevalidate} disabled={revalidating} data-testid="kb-revalidate-all"
                >
                    {revalidating
                        ? <><ArrowsClockwise size={12} className="mr-1 animate-spin" /> Re-validating…</>
                        : <><ShieldWarning size={12} className="mr-1" /> Re-validate all drafts</>}
                </Button>
                <Button
                    variant="ghost" size="sm" className="rounded-sm h-8 text-[11px] text-muted-foreground hover:text-red-600"
                    onClick={onWipe} data-testid="kb-clear"
                >
                    <Trash size={12} className="mr-1" /> Clear
                </Button>
            </div>
        </div>
    );
}

function EmptyState({ onFile, uploading, onPaste }) {
    return (
        <div className="border border-dashed border-amber-500/50 bg-amber-500/5 rounded-sm p-5" data-testid="kb-empty">
            <div className="flex items-start gap-3 mb-4">
                <ShieldWarning size={20} weight="duotone" className="text-amber-600 dark:text-amber-300 mt-0.5 shrink-0" />
                <div>
                    <p className="font-display text-sm font-semibold">No Knowledge Base configured</p>
                    <p className="text-[12px] text-muted-foreground mt-1">
                        Content is being generated WITHOUT fact-grounding. Unsupported claims (specific demographics, match cadences, pricing, markets) cannot be detected until you upload an authoritative document.
                    </p>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept=".pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain"
                        onChange={onFile}
                        className="hidden"
                        data-testid="kb-upload-file"
                    />
                    <span className={`inline-flex items-center gap-1 rounded-sm bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider hover:bg-primary/90 ${uploading ? "opacity-50" : ""}`}>
                        <UploadSimple size={12} /> {uploading ? "Uploading…" : "Upload PDF / Markdown / Text"}
                    </span>
                </label>
                <Button
                    variant="outline" size="sm" className="rounded-sm h-8 text-[11px]"
                    onClick={onPaste} data-testid="kb-paste"
                >
                    <FileText size={12} className="mr-1" /> Paste text
                </Button>
            </div>
        </div>
    );
}

function PasteDialog({ open, onOpenChange, onSaved }) {
    const [title, setTitle] = useState("HAEVN Knowledge Base");
    const [text, setText] = useState("");
    const [sourceType, setSourceType] = useState("markdown");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        if (!text.trim()) {
            toast.error("Paste the Knowledge Base text first");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/content-studio/knowledge-base/text", {
                title: title.trim() || "HAEVN Knowledge Base",
                text,
                source_type: sourceType,
            });
            toast.success("Knowledge Base updated", {
                description: `${r.data.active.word_count} words · v${r.data.active.version}`,
            });
            onSaved(r.data.active);
            setText("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-2xl" data-testid="kb-paste-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Paste Knowledge Base text</DialogTitle>
                    <DialogDescription className="text-[12px]">
                        Replaces the current active document. The previous version is archived (not deleted) and remains in audit history.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Title</label>
                        <Input
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            className="rounded-sm h-9"
                            data-testid="kb-paste-title"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Format</label>
                        <div className="flex items-center gap-3">
                            <label className="text-[12px] flex items-center gap-1.5 cursor-pointer">
                                <input type="radio" name="kb-type" checked={sourceType === "markdown"} onChange={() => setSourceType("markdown")} data-testid="kb-type-markdown" />
                                Markdown
                            </label>
                            <label className="text-[12px] flex items-center gap-1.5 cursor-pointer">
                                <input type="radio" name="kb-type" checked={sourceType === "text"} onChange={() => setSourceType("text")} data-testid="kb-type-text" />
                                Plain text
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">Knowledge Base content</label>
                        <Textarea
                            rows={14}
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder={"# HAEVN Overview\n\nHAEVN is an intent-based matching network…\n\n# Markets\n\nCurrently operating in Austin, Texas.\n\n# Pricing\n\nNot yet public."}
                            className="rounded-sm font-mono text-[12px]"
                            data-testid="kb-paste-textarea"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                            {text.length} chars
                        </p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button size="sm" className="rounded-sm" onClick={save} disabled={busy} data-testid="kb-paste-save">
                        {busy ? "Saving…" : "Replace Knowledge Base"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PreviewDialog({ open, onOpenChange, doc }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-3xl" data-testid="kb-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{doc?.title || "Knowledge Base"}</DialogTitle>
                    <DialogDescription className="text-[12px] font-mono">
                        v{doc?.version} · {doc?.word_count} words · {(doc?.chunks || []).length} chunks
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto border border-border rounded-sm p-4 bg-background">
                    <pre className="text-[12px] whitespace-pre-wrap font-mono leading-relaxed">
                        {doc?.full_text || "(empty)"}
                    </pre>
                </div>
                <DialogFooter>
                    <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
